import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import '@/styles/charts.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  TooltipProps
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

interface TaskDistributionData {
  time: string;
  maintenance: number;
  repairs: number;
  inspections: number;
  cleaning: number;
}

const data: TaskDistributionData[] = [
  { time: '6-9 AM', maintenance: 5, repairs: 2, inspections: 3, cleaning: 4 },
  { time: '9-12 PM', maintenance: 8, repairs: 5, inspections: 4, cleaning: 6 },
  { time: '12-3 PM', maintenance: 6, repairs: 7, inspections: 3, cleaning: 5 },
  { time: '3-6 PM', maintenance: 7, repairs: 4, inspections: 5, cleaning: 3 },
  { time: '6-9 PM', maintenance: 4, repairs: 3, inspections: 2, cleaning: 2 },
];

// Custom color palette
const colors = {
  maintenance: 'hsl(221.2 83.2% 53.3%)', // primary blue
  repairs: 'hsl(15 90% 65%)',            // warm orange
  inspections: 'hsl(142 72% 29%)',       // deep green
  cleaning: 'hsl(262 83% 58%)'           // vibrant purple
};

const CustomXAxis = (props: any) => (
  <XAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

const CustomYAxis = (props: any) => (
  <YAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

export function TaskDistribution() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Task Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <CustomXAxis dataKey="time" />
              <CustomYAxis />
              <Tooltip
                content={({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
                  if (active && payload?.length) {
                    // Convert all values to numbers before summing
                    const total = payload.reduce((sum, entry) => {
                      // Handle different value types safely
                      const numValue = typeof entry.value === 'number' 
                        ? entry.value 
                        : (Array.isArray(entry.value) ? 0 : Number(entry.value) || 0);
                      return sum + numValue;
                    }, 0);
                    
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-sm">
                        <div className="text-sm font-medium">{label}</div>
                        <div className="mt-1 space-y-1">
                          {payload.map((entry) => (
                            <div
                              key={entry.name}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className={`chart-color-indicator color-${typeof entry.name === 'string' ? entry.name.toLowerCase().replace(/\s+/g, '-') : 'default'}`}
                              />
                              <span className="text-muted-foreground">
                                {entry.name}: {entry.value} tasks
                              </span>
                            </div>
                          ))}
                          <div className="pt-1 border-t text-sm text-muted-foreground">
                            Total: {total} tasks
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Bar
                dataKey="maintenance"
                name="Maintenance"
                stackId="a"
                fill={colors.maintenance}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="repairs"
                name="Repairs"
                stackId="a"
                fill={colors.repairs}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="inspections"
                name="Inspections"
                stackId="a"
                fill={colors.inspections}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="cleaning"
                name="Cleaning"
                stackId="a"
                fill={colors.cleaning}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}